import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Typography,
  Card,
  CardContent,
  Avatar,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from "chart.js";
import { toast, ToastContainer } from "react-toastify";
import { listCourses } from "../../api/assessmentHierarchy";
import { listCategories } from "../../api/assessmentCategories";
import { getStudentDetail, apiErrorMessage } from "../../api/assessmentStudents";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
);

const PURPLE = "#5b52e6";
const PINK = "#ec4899";
const ACCENT = "#17479e";

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const formatPercent = (value) =>
  value == null || Number.isNaN(Number(value)) ? "—" : `${Math.round(Number(value) * 10) / 10}%`;

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatShortDate = (value) =>
  new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

function CardTitle({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Box sx={{ width: 4, height: 18, borderRadius: 1, backgroundColor: PURPLE }} />
      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: PURPLE }}>{children}</Typography>
    </Box>
  );
}

function StatTile({ value, label }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: "#eef0fc",
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: PURPLE }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
        {label}
      </Typography>
    </Box>
  );
}

const STATUS_COLORS = {
  COMPLETED: { color: "#2e7d32", bg: "#e8f5e9" },
  SUBMITTED: { color: "#2e7d32", bg: "#e8f5e9" },
  IN_PROGRESS: { color: "#ed6c02", bg: "#fff4e5" },
};

function AttemptStatusChip({ value }) {
  const cfg = STATUS_COLORS[value] ?? { color: "#555", bg: "#f0f0f0" };
  const label = (value || "—")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

// Score progression for the attempts currently on screen, oldest -> newest.
const scoreProgressionData = (attempts) => ({
  labels: attempts.map((a) => formatShortDate(a.startedAt)),
  datasets: [
    {
      data: attempts.map((a) => a.scorePercentage),
      borderColor: PURPLE,
      backgroundColor: PURPLE,
      borderWidth: 2.5,
      pointBackgroundColor: PURPLE,
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      tension: 0.4,
    },
  ],
});

const scoreProgressionOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(255,255,255,0.95)",
      titleColor: "#1a1a2e",
      bodyColor: "#555",
      borderColor: "#e0e0e0",
      borderWidth: 1,
      padding: 10,
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      max: 100,
      grid: { color: "#f0f0f0" },
      ticks: { stepSize: 25, color: "#9e9e9e", font: { size: 12 } },
    },
  },
};

function PageSkeleton() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, p: 3 }}>
          <Skeleton variant="circular" width={64} height={64} sx={{ mb: 2 }} />
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="50%" />
        </Card>
        <Card sx={{ boxShadow: 1, borderRadius: 3, p: 3 }}>
          <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
        </Card>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, p: 3 }}>
          <Skeleton variant="rounded" height={260} />
        </Card>
        <Card sx={{ boxShadow: 1, borderRadius: 3, p: 3 }}>
          <Skeleton variant="rounded" height={180} />
        </Card>
      </Box>
    </Box>
  );
}

function TopicList({ topics, icon, emptyText }) {
  if (!topics?.length) {
    return (
      <Typography color="text.secondary" variant="body2">
        {emptyText}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {topics.map((topic) => (
        <Box key={topic.topicId} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {icon}
          <Typography sx={{ fontSize: "0.85rem", flex: 1 }}>{topic.topicName}</Typography>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "text.secondary" }}>
            {formatPercent(topic.percentCorrect)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function AssessmentStudentPerformance() {
  const { studentId } = useParams();
  // The list screen passes the course it was filtered by as ?courseId=.
  const [searchParams, setSearchParams] = useSearchParams();
  const courseId = searchParams.get("courseId") || "";
  const categoryId = searchParams.get("categoryId") || "";

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")))
      .finally(() => setCoursesLoaded(true));
  }, []);

  // No course in the URL: default to the first one so the page is never empty.
  useEffect(() => {
    if (!courseId && coursesLoaded && courses.length > 0) {
      setSearchParams({ courseId: String(courses[0].id) }, { replace: true });
    }
  }, [courseId, coursesLoaded, courses, setSearchParams]);

  // Load the selected course's categories; if the URL has no category (or one from another
  // course), default to the first so the Target tile is meaningful.
  useEffect(() => {
    setCategories([]);
    setCategoriesLoaded(false);
    if (!courseId) return undefined;
    let cancelled = false;
    listCategories({ courseId, isActive: true, size: 100 })
      .then((data) => {
        if (cancelled) return;
        setCategories(data.content);
        setCategoriesLoaded(true);
        const stillValid = data.content.some((c) => String(c.id) === categoryId);
        if (!stillValid) {
          const next = new URLSearchParams(searchParams);
          if (data.content.length > 0) next.set("categoryId", String(data.content[0].id));
          else next.delete("categoryId");
          setSearchParams(next, { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(apiErrorMessage(err, "Failed to load courses"));
      });
    return () => {
      cancelled = true;
    };
    // Depend only on courseId: re-running when this effect rewrites the URL would cause extra fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => {
    if (!courseId) {
      setLoading(!coursesLoaded);
      return;
    }
    setLoading(true);
    let cancelled = false;
    getStudentDetail(studentId, { courseId, categoryId, page, size: rowsPerPage })
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setDetail(null);
        toast.error(apiErrorMessage(err, "Failed to load student performance"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, courseId, categoryId, page, rowsPerPage, coursesLoaded]);

  const handleCourseChange = (value) => {
    setPage(0);
    setSearchParams({ courseId: String(value) });
  };

  const attempts = useMemo(() => detail?.attempts?.content || [], [detail]);
  const readiness = detail?.readiness;

  const chartAttempts = useMemo(
    () =>
      attempts
        .filter((a) => a.scorePercentage != null && a.startedAt)
        .slice()
        .sort((a, b) => new Date(a.startedAt) - new Date(b.startedAt)),
    [attempts],
  );

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <ToastContainer />
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          to="/assessment"
          onClick={() => localStorage.setItem("assessmentTabLabel", "Students")}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          Assessment
        </Link>
        <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
          Student Performance
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2.5 }}>
        {categoriesLoaded && categories.length === 0 && courseId ? (
          <Typography sx={{ alignSelf: "center", fontSize: "0.75rem", color: "text.secondary" }}>
            No categories for this course - showing the default 85% target.
          </Typography>
        ) : null}
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Exam</InputLabel>
          <Select
            label="Exam"
            value={courses.some((c) => String(c.id) === courseId) ? courseId : ""}
            onChange={(e) => handleCourseChange(e.target.value)}
          >
            {courses.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Course</InputLabel>
          <Select
            label="Course"
            value={categories.some((c) => String(c.id) === categoryId) ? categoryId : ""}
            onChange={(e) => {
              setPage(0);
              const next = new URLSearchParams(searchParams);
              next.set("categoryId", String(e.target.value));
              setSearchParams(next);
            }}
          >
            {categories.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <PageSkeleton />
      ) : !courseId ? (
        <Typography color="text.secondary">Select an exam to view performance.</Typography>
      ) : !detail ? (
        <Typography color="text.secondary">Student not found.</Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <CardTitle>Profile</CardTitle>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      mb: 2,
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      bgcolor: PINK,
                      color: "#fff",
                    }}
                  >
                    {initials(detail.studentName)}
                  </Avatar>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.15rem" }}>
                    {detail.studentName || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", color: "text.disabled" }}>
                    {courses.find((c) => String(c.id) === courseId)?.name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                    mt: 2.5,
                  }}
                >
                  <StatTile value={detail.testsAttempted} label="Tests" />
                  <StatTile value={formatPercent(detail.avgScorePercentage)} label="Avg Score" />
                  <StatTile value={formatPercent(detail.completionRate)} label="Completion" />
                  <StatTile
                    value={formatPercent(readiness?.readinessPercentage)}
                    label="Readiness"
                  />
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <CardTitle>Weak Topics</CardTitle>
                <TopicList
                  topics={readiness?.weakTopics}
                  icon={<ReportProblemOutlinedIcon sx={{ fontSize: 18, color: "#ef4444" }} />}
                  emptyText="No weak topics yet"
                />
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <CardTitle>Strong Topics</CardTitle>
                <TopicList
                  topics={readiness?.strongTopics}
                  icon={<TaskAltOutlinedIcon sx={{ fontSize: 18, color: "#22c55e" }} />}
                  emptyText="No strong topics yet"
                />
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {readiness && (
              <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <CardTitle>Exam Readiness</CardTitle>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                      gap: 1.5,
                      mb: 2.5,
                    }}
                  >
                    <StatTile value={formatPercent(readiness.readinessPercentage)} label="Readiness" />
                    <StatTile value={formatPercent(readiness.readinessTargetPercentage)} label="Target" />
                    <StatTile
                      value={`${readiness.topicsCoveredCount}/${readiness.totalTopicsInCourse}`}
                      label="Topics Covered"
                    />
                    <StatTile value={readiness.practiceQuestionsAnswered} label="Practice Questions" />
                  </Box>
                  <Box
                    sx={{
                      position: "relative",
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#eef0f2",
                      overflow: "visible",
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.min(100, Math.max(0, readiness.readinessPercentage || 0))}%`,
                        borderRadius: 4,
                        backgroundColor: PURPLE,
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: -4,
                        left: `${Math.min(100, Math.max(0, readiness.readinessTargetPercentage || 0))}%`,
                        width: 2,
                        height: 16,
                        backgroundColor: PINK,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <CardTitle>Score Progression</CardTitle>
                {chartAttempts.length === 0 ? (
                  <Box
                    sx={{
                      height: 260,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary" variant="body2">
                      No scored attempts on this page
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ height: 260 }}>
                    <Line
                      data={scoreProgressionData(chartAttempts)}
                      options={scoreProgressionOptions}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 1, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <CardTitle>Attempt History</CardTitle>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Test", "Score", "Started At", "Status"].map((h) => (
                          <TableCell
                            key={h}
                            sx={{ backgroundColor: ACCENT, color: "white", fontWeight: 600, padding: "10px 12px" }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attempts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: "center", py: 3 }}>
                            <Typography color="text.secondary" variant="body2">
                              No attempts in this course
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        attempts.map((a, i) => (
                          <TableRow key={`${a.startedAt}-${i}`} hover>
                            <TableCell sx={{ padding: "10px 12px", fontWeight: 600, fontSize: "0.85rem" }}>
                              {a.testName || "—"}
                            </TableCell>
                            <TableCell sx={{ padding: "10px 12px" }}>{formatPercent(a.scorePercentage)}</TableCell>
                            <TableCell sx={{ padding: "10px 12px" }}>{formatDateTime(a.startedAt)}</TableCell>
                            <TableCell sx={{ padding: "10px 12px" }}>
                              <AttemptStatusChip value={a.status} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={detail.attempts?.totalElements ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AssessmentStudentPerformance;
